/** Colecția de tronsoane a proiectului.
 *
 *  UN TRONSON e modulul din care se compune o bară: două sau mai multe
 *  apartamente de o parte și de alta a unui coridor, cu sau fără nucleu de
 *  scară. Din ele compune planul de situație barele, și din ele desenează
 *  arhitectura etajele — deci ecranul ăsta e o INTRARE în calcul, nu un
 *  rezultat al lui. De-aia stă în rail înaintea pipeline-ului.
 *
 *  CE SE ARATĂ: tot ce are proiectul, CIORNELE INCLUSE. Nu e totuna cu «ce ar
 *  folosi o generare pornită acum» — aia cere `published`, iar întrebarea asta o
 *  pun motoarele, nu ecranul. Colecția răspunde la «ce module am», iar un șablon
 *  salvat ca nou e o ciornă: lipsind de-aici, arăta ca și cum salvarea nu s-ar fi
 *  scris. Ciorna se vede cu chipul ei și se poate ascunde dintr-un comutator.
 *
 *  COLECȚIA E O VEDERE, nu o copie. Un proiect nou n-are niciun modul al lui:
 *  vede cele șapte globale, desenate din planșe. Când unul e rescris aici, se
 *  scrie un rând nou cu ACELAȘI COD și o versiune mai mare, legat de proiect, și
 *  el îl acoperă pe cel global — numai pentru proiectul ăsta. Restul rămân
 *  globale și primesc de la sine corecțiile făcute în catalog.
 *
 *  TREI CATEGORII, NU CINCI. Baza ține `role` cu cinci valori, dintre care
 *  «core» e un MIJLOC CU NUCLEU, nu o a patra categorie. Traducerea o face
 *  serverul (`api/catalog.py`), ca să nu existe două definiții ale aceluiași
 *  lucru; ecranul doar grupează și scrie numele.
 */
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as api from "../api";
import ObsHost from "../structure/ObsHost";
import { BandaDoarCitire, useRolProiect } from "../components/rol";
import TronsonView from "../viewer/tronson/TronsonView";
import type { Ales } from "../viewer/tronson/Desen";
import PanouProprietati from "../viewer/tronson/PanouProprietati";
import ImparteModul from "../viewer/tronson/ImparteModul";
import TronsonNou from "../viewer/tronson/TronsonNou";
import ImporturiInLucru from "../viewer/tronson/ImporturiInLucru";
import Etichete from "../viewer/tronson/Etichete";
import { deriva, type TronsonDoc } from "../viewer/tronson/doc";
import { numar, useT, useTn } from "../i18n";
import type { Cheie } from "../i18n";
import "../viewer/tronson/colectie.css";
import DialogRedenumire from "../components/DialogRedenumire";
import MeniuExport, { type FormatExport } from "../viewer/tronson/export/MeniuExport";
import { exportaTronson } from "../viewer/tronson/export/exporta";

// `three` intră în bundle doar când cineva chiar deschide macheta — aceeași
// regulă ca la vizualizatorul de ansamblu.
const Randare3D = lazy(() => import("../viewer/tronson/Randare3D"));

const CATEGORII: readonly { cheie: string; titlu: Cheie; sub: Cheie }[] = [
  { cheie: "capat", titlu: "colectie.capat", sub: "colectie.capat_sub" },
  { cheie: "mijloc", titlu: "colectie.mijloc", sub: "colectie.mijloc_sub" },
  { cheie: "colt", titlu: "colectie.colt", sub: "colectie.colt_sub" },
  { cheie: "izolat", titlu: "colectie.izolat", sub: "colectie.izolat_sub" },
];

/** Unde stă modulul în bară, desenat: rândul de module, cu al categoriei în
 *  accent. Titlul singur («Mijloc») nu spune din ce ansamblu e mijlocul;
 *  desenul arată bara întreagă și locul lui în ea dintr-o privire. */
function PozitiaInBara({ cheie }: { cheie: string }) {
  const m = (x: number, y: number, pe: boolean, w = 12, h = 12) => (
    <rect key={`${x}-${y}`} x={x} y={y} width={w} height={h} rx={2}
          className={pe ? "pe" : undefined} />
  );
  let piese: JSX.Element[];
  if (cheie === "capat") piese = [0, 14, 28, 42].map((x, i) => m(x, 8, i === 0));
  else if (cheie === "mijloc") piese = [0, 14, 28, 42].map((x, i) => m(x, 8, i === 1 || i === 2));
  else if (cheie === "colt") {
    piese = [m(0, 0, false), m(14, 0, false), m(28, 0, true), m(28, 14, false)];
  } else piese = [m(19, 4, true, 16, 16)];
  return (
    <svg className="ob-tr-cat-poz" width={54} height={28} viewBox="0 0 54 28"
         aria-hidden="true">{piese}</svg>
  );
}

export function Cartonas({ t: tronson, activ, la, laEditare, laStergere,
                           laImpartire, laRedenumire, laExport }: {
  t: api.Tronson; activ: boolean; la: () => void; laEditare: () => void;
  /** Planșa modulului ca PDF sau DWG. Lipsă = nu se exportă de aici. */
  laExport?: (format: FormatExport) => void;
  /** Deschide «cine mai are modulul». Lipsă = nu e al meu, deci nu-l pot da
   *  mai departe (v. `services/acces_tronson.poate_imparti`). */
  laImpartire?: () => void;
  /** Șterge ciorna proiectului. Lipsă = nu se poate (drept de citire, sau
   *  modul global, care n-are ce ciornă să piardă). */
  laStergere?: () => void;
  /** Alt nume pentru modul. Lipsă = nu e al meu, deci nu-l pot redenumi. */
  laRedenumire?: () => void;
}) {
  const t = useT();
  /** Câte camere are fiecare apartament, ca «2+2 cam» sau «3+1+1+1 cam». */
  const mixScris = (camere: number[]) =>
    camere.length ? t("colectie.camere", { mix: camere.join("+") }) : "—";
  // O CIORNĂ POATE SĂ N-AIBĂ `alpha`, ȘI ASTA DOBORA TOATĂ PAGINA.
  //
  // `alpha` e vederea derivată — variante, mix, fronturi — și o pune serverul
  // DOAR dacă documentul trece probele. O ciornă n-are de ce să le treacă: aia e
  // starea ei, lucru la jumătate. Când salvezi un modul ca nou dintr-un desen
  // neterminat, rândul intră cu `alpha = {}`.
  //
  // Cât timp colecția arăta numai module publicate, rândurile astea nu ajungeau
  // niciodată aici. De când arată și ciorne, ajung — iar `a.variants.find(…)` pe
  // `undefined` aruncă în timpul randării, deci cade ÎNTREGUL ecran, nu doar
  // cartonașul: «după ce am salvat un modul ca nou, nu-mi mai apare nimic la
  // templates». Lista venea bine de la server; pagina se rupea la desenat.
  //
  // Deci cartonașul citește apărat și spune ce nu știe. O ciornă fără alpha nu e
  // o eroare — e un modul care n-a fost încă verificat.
  const a = tronson.alpha ?? ({} as Partial<api.TronsonAlpha>);
  const variante = a.variants ?? [];
  const util = (variante.find((v) => v.default) ?? variante[0])?.useful_m2;
  const camere = Object.entries(a.mix ?? {})
    .flatMap(([cam, n]) => Array<number>(n).fill(Number(cam)))
    .sort((x, y) => y - x);
  /** Ciornă fără vedere derivată: nu i se pot scrie cifrele, doar spus de ce. */
  const neverificat = !a.variants || !a.mix || !a.front;
  return (
    // DUBLU CLIC DESCHIDE EDITORUL — gestul din orice unealtă de desen. Simplu
    // clic alege, dublu deschide; iar `onDoubleClick` primește oricum și cele
    // două `onClick` de dinaintea lui, deci deschiderea vine peste o selecție
    // deja făcută și nu se pierde nimic.
    <button className={`ob-card ob-tr-card${activ ? " on" : ""}`}
            onClick={la} onDoubleClick={laEditare}
            title={t("colectie.dublu_clic")}>
      <div className="ob-tr-card-cap">
        <b>{tronson.code}</b>
        {tronson.has_core && (
          <span className="ob-chip ob-c-acc">{t("colectie.nucleu")}</span>
        )}
        {/* O CIORNĂ SE SPUNE ÎNAINTE DE ORICE ALTCEVA. E salvată, dar niciun
            plan n-o citește: motoarele cer `published`. Fără chip, un modul
            neterminat ar sta în colecție arătând exact ca unul care chiar
            intră în bară. */}
        {tronson.status === "draft" ? (
          <span className="ob-chip ob-c-amb"
                title={t("colectie.ciorna_titlu", { versiune: tronson.version })}>
            {t("colectie.ciorna", { versiune: tronson.version })}
          </span>
        ) : tronson.scope === "proiect" && (
          <span className="ob-chip ob-c-amb"
                title={t("colectie.editat_titlu", { versiune: tronson.version })}>
            {t("colectie.editat", { versiune: tronson.version })}
          </span>
        )}
        {/* ÎMPĂRȚIT CU MINE: modulul e al altcuiva, iar asta schimbă ce pot
            face cu el — de-aia se spune pe cartonaș, nu doar în panou. */}
        {tronson.scope === "impartit" && (
          <span className="ob-chip" title={t("colectie.impartit_titlu")}>
            {t("colectie.impartit")}
          </span>
        )}
      </div>
      <div className="ob-tr-card-nume">{a.label ?? tronson.code}</div>
      {/* AUTORUL. Într-o colecție cu module de la mai mulți oameni, «al cui e»
          e prima întrebare; lipsește pe colecția unui proiect, unde n-are
          înțeles. */}
      {tronson.autor && (
        <div className="ob-tr-card-autor">{tronson.autor}</div>
      )}
      {/* ETICHETELE, pe cartonaș. Cifrele de dedesubt spun cât e modulul;
          etichetele spun la ce e — și ele sunt lucrul după care cineva se uită
          peste colecție când caută «alea sociale». Doar de citit: cartonașul e
          un buton, iar un gest de scris în el ar fi și o apăsare pe el. Fără
          niciuna nu se scrie nimic — un «fără etichete» pe fiecare cartonaș ar
          fi zgomot pe toată colecția. */}
      {tronson.etichete?.length > 0 && (
        <Etichete etichete={tronson.etichete} doarCitire />
      )}
      {neverificat ? (
        <div className="ob-tr-card-cifre">
          <span className="ob-tr-card-neverificat">
            {t("colectie.neverificat")}
          </span>
        </div>
      ) : (
        <div className="ob-tr-card-cifre">
          <span><b>{a.flats}</b> {t("colectie.apartamente")}</span>
          <span>{mixScris(camere)}</span>
          {util != null && (
            <span>
              <b>{numar(util, { minimumFractionDigits: 1,
                                maximumFractionDigits: 1 })}</b>
              {" "}{t("colectie.util")}
            </span>
          )}
        </div>
      )}
      {a.front && (
        <div className="ob-tr-card-front">
          {t("colectie.front", { metri: metri(a.front.nominal_m) })}
          {a.front.rigid
            ? t("colectie.front_rigid")
            : t("colectie.front_gama", { minim: metri(a.front.min_m),
                                         maxim: metri(a.front.max_m) })}
        </div>
      )}
      {/* Butonul e pentru cine nu ghicește dublul clic — aceeași acțiune, scrisă.
          `stopPropagation` ca apăsarea lui să nu treacă și ca selecție. */}
      <span className="ob-tr-card-actiuni">
        <span className="ob-tr-card-edit"
              role="button" tabIndex={0}
              onClick={(e) => { e.stopPropagation(); laEditare(); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); laEditare(); } }}>
          {t("colectie.editeaza")}
        </span>
        {/* ÎMPĂRȚIREA, tot de pe cartonaș: e o însușire a modulului, ca
            ștergerea, nu ceva ce se caută prin editor. */}
        {laImpartire && (
          <span className="ob-tr-card-edit"
                role="button" tabIndex={0}
                onClick={(e) => { e.stopPropagation(); laImpartire(); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); laImpartire(); } }}>
            {t("colectie.imparte")}
          </span>
        )}
        {/* REDENUMIREA, tot de-aici: numele se alegea o singură dată, la
            naștere, iar o greșeală de tastare cerea un modul nou și mutarea
            lucrului («add the ability to rename the tronson once it's up»,
            13.10.2026). */}
        {laRedenumire && (
          <span className="ob-tr-card-edit"
                role="button" tabIndex={0}
                onClick={(e) => { e.stopPropagation(); laRedenumire(); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); laRedenumire(); } }}>
            {t("colectie.redenumeste")}
          </span>
        )}
        {/* EXPORTUL, de pe cartonaș: planșa se scoate de unde se vede modulul,
            fără să-l deschizi — perechea importului, care tot de pe colecție
            intră («Tronson nou» → planșă). Pe orice modul, și pe cele doar
            de citit: exportul nu scrie nimic. */}
        {laExport && <MeniuExport compact laExport={laExport} />}
        {/* ȘTERGEREA CIORNEI, de pe cartonaș: o ciornă lăsată la jumătate se
            strânge de unde se vede, nu deschizând editorul ca s-o arunci. */}
        {laStergere && tronson.status === "draft" && (
          <span className="ob-tr-card-sterge"
                role="button" tabIndex={0}
                onClick={(e) => { e.stopPropagation(); laStergere(); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); laStergere(); } }}>
            {t("colectie.sterge_ciorna")}
          </span>
        )}
      </span>
    </button>
  );
}

/** Metri cu două zecimale, în convenția limbii — 6,20 m sau 6.20 m. */
function metri(v: number): string {
  return numar(v, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Vizualizatorul unui modul: plan sau machetă, plus panoul de proprietăți. */
function Vizualizator({ doc, cod, etichete, posibile, laEtichete, lucrez }: {
  doc: TronsonDoc; cod: string;
  /** Etichetele modulului deschis, și vocabularul din care se mai poate alege.
   *  Se dau de sus fiindcă tot de sus se reîncarcă colecția după o scriere:
   *  cartonașul și panoul arată același lucru și n-au voie să se despartă. */
  etichete: string[]; posibile: api.Eticheta[];
  /** Lipsă = drept de citire: panoul le arată, nu le schimbă. */
  laEtichete?: (etichete: string[]) => void;
  lucrez?: boolean;
}) {
  const t = useT();
  const [vedere, setVedere] = useState<"2d" | "3d">("2d");
  /** Planșa tehnică sau amenajarea — aceeași împărțire ca în editor, și din
   *  același motiv: un modul amenajat poartă finisaje, mobilier și instalație,
   *  iar dacă vizualizatorul n-ar ști să le arate, munca din editor ar fi
   *  vizibilă doar în editor. */
  const [mod, setMod] = useState<"plan" | "design">("plan");
  const [ales, setAles] = useState<Ales>(null);
  const plan = useMemo(() => deriva(doc), [doc]);
  // Selecția e a PLANULUI: în machetă nu se poate arăta, iar una purtată
  // dintr-o vedere în alta ar rămâne pe un perete pe care nimic nu-l
  // evidențiază. Se golește la comutare.
  useEffect(() => { setAles(null); }, [vedere, cod]);
  // Modul se întoarce la planșă la schimbarea modulului: «Design» pe unul care
  // n-a fost amenajat arată pardoseli implicite și nicio instalație, ceea ce se
  // citește ca o vedere goală, nu ca un modul nemobilat.
  useEffect(() => { setMod("plan"); }, [cod]);

  return (
    <div className="ob-tr-viz">
      <div className="ob-tr-bara">
        <div className="ob-seg">
          {([["plan", "colectie.vedere_plan"],
             ["design", "colectie.vedere_design"],
             ["machete", "colectie.vedere_macheta"]] as const).map(([k, n2]) => (
            <button key={k}
                    className={(k === "machete" ? vedere === "3d"
                      : vedere === "2d" && mod === k) ? "on" : ""}
                    onClick={() => {
                      if (k === "machete") { setVedere("3d"); return; }
                      setVedere("2d");
                      setMod(k);
                    }}>{t(n2)}</button>
          ))}
        </div>
        <span className="ob-tr-nota-scara">
          {t("colectie.scara", { metri: metri(doc.front.nominal_m) })}
        </span>
      </div>
      <div className="ob-tr-panza">
        {vedere === "2d"
          ? <TronsonView doc={doc} ales={ales} laAles={setAles} mod={mod} />
          : (
            <Suspense fallback={<div className="ob-tr-astept">
                                  {t("colectie.se_incarca_macheta")}
                                </div>}>
              <Randare3D doc={doc} mod={mod} />
            </Suspense>
          )}
      </div>
      <PanouProprietati doc={doc} plan={plan} ales={vedere === "2d" ? ales : null}
                        etichete={etichete} posibileEtichete={posibile}
                        laEtichete={laEtichete} scriuEtichete={lucrez} />
    </div>
  );
}

/** DE UNDE VINE MODULUL, sub desenul lui.
 *
 *  Un modul din colecție e un desen pe care de cele mai multe ori nu l-ai
 *  făcut tu: e al altcuiva, sau e transcris dintr-o planșă de acum doi ani.
 *  «De unde știm asta?» e prima întrebare, iar până acum răspunsul exista în
 *  bază (`doc.provenance`, scris la naștere de importator) și nu-l vedea
 *  nimeni.
 *
 *  Sunt DOUĂ proveniențe, și se citesc împreună: una a DESENULUI — ce planșă,
 *  ce scară — și una a RÂNDULUI — al cui e, cine l-a scris, când.
 */
export function Provenienta({ doc, autor, creatDe, creatLa, nota }: {
  doc: TronsonDoc;
  autor?: string | null;
  creatDe?: string | null;
  creatLa?: string | null;
  nota?: string | null;
}) {
  const t = useT();
  const p = doc.provenance ?? {};
  const randuri: [string, string][] = [];
  if (autor) randuri.push([t("prov.al_cui"), autor]);
  // Cine a desenat, doar când e ALTCINEVA decât cel care îl are: altfel ar fi
  // același nume scris de două ori, una sub alta.
  if (creatDe && creatDe !== autor) randuri.push([t("prov.desenat_de"), creatDe]);
  if (p.source) randuri.push([t("prov.sursa"), p.source]);
  if (p.measured) randuri.push([t("prov.masurat"), p.measured]);
  if (nota) randuri.push([t("prov.nota"), nota]);
  if (p.note && p.note !== nota) randuri.push([t("prov.nota_desenului"), p.note]);
  if (creatLa) {
    randuri.push([t("prov.scris_la"),
                  new Date(creatLa).toLocaleDateString("ro-RO",
                    { day: "numeric", month: "long", year: "numeric" })]);
  }
  if (!randuri.length) return null;

  return (
    <details className="ob-prov">
      <summary>{t("prov.titlu")}</summary>
      <dl>
        {randuri.map(([cheie, val]) => (
          <div key={cheie}><dt>{cheie}</dt><dd>{val}</dd></div>
        ))}
      </dl>
    </details>
  );
}

export default function Colectie() {
  /** FĂRĂ PROIECT E COLECȚIA OMULUI. Aceeași pagină slujește ambele: ce se
   *  vede e o listă de module cu aceleași cartonașe, iar singura deosebire e
   *  de unde vine lista și unde duce apăsarea pe «editează». Două pagini ar fi
   *  însemnat două locuri de ținut la zi pentru un ecran identic. */
  const { projectId = "" } = useParams();
  const alMeu = !projectId;
  /** Unde stă editorul modulului — sub proiect sau sub colecția mea. */
  const caleaEditorului = (c: string) => (alMeu
    ? `/tronsoane/${encodeURIComponent(c)}/edit`
    : `/p/${projectId}/templates/${encodeURIComponent(c)}/edit`);
  const navigate = useNavigate();
  const t = useT();
  const tn = useTn();
  const [colectie, setColectie] = useState<api.ColectieTronsoane | null>(null);
  /** Vocabularul de etichete al proiectului — din ce se poate alege. */
  const [posibile, setPosibile] = useState<api.Eticheta[]>([]);
  /** O scriere de etichete e pe drum: butoanele panoului se sting. */
  const [scriuEtichete, setScriuEtichete] = useState(false);
  const [eroare, setEroare] = useState("");
  const [cod, setCod] = useState<string | null>(null);
  const [doc, setDoc] = useState<TronsonDoc | null>(null);
  /** Proveniența rândului deschis — vine odată cu documentul. */
  const [prov, setProv] = useState<{
    creat_de?: string | null; creat_la?: string | null; nota?: string | null;
  }>({});
  const [incarcDoc, setIncarcDoc] = useState(false);
  const { potScrie: potScrieProiect } = useRolProiect();
  // În colecția MEA scriu oricum — rolul de pe proiect n-are ce spune
  // despre ea. Ce n-am voie să scriu e oprit de server, pe grant.
  const potScrie = alMeu || potScrieProiect;
  /** Ce s-a întâmplat ultima dată — «ciorna M9 a fost ștearsă». */
  const [nota, setNota] = useState("");
  /** Lista se recitește după o ștergere: numărătorul de ciorne și chipul
   *  cartonașului vin de la server, nu se ghicesc aici. */
  const [reincarcare, setReincarcare] = useState(0);
  /** Citirile pornite în sesiunea asta, ca bara să apară pe loc. Lista
   *  adevărată vine de la server (v. `ImporturiInLucru`) — asta e doar ca
   *  secundele dintre pornire și primul răspuns să nu arate a nimic. */
  const [pornite, setPornite] = useState<
    { cod: string; la: number; eroare?: string }[]>([]);
  /** Modulul pe care-l redenumim, și numele nou tastat. */
  const [deRedenumit, setDeRedenumit] = useState<string | null>(null);
  const [redenumesc, setRedenumesc] = useState(false);
  /** Codul al cărui panou de împărțire e deschis. */
  const [deImpartit, setDeImpartit] = useState<string | null>(null);

  /** NUMELE E ȘI IDENTITATEA modulului — hârtia de calc, etichetele, cine-l
   *  mai are, și `id`-ul din document. Serverul le mută pe toate odată; ecranul
   *  nu are decât să ceară și să reîncarce colecția. */
  const redenumeste = async (nou: string) => {
    const vechi = deRedenumit;
    if (!vechi || !api.NUME_MODUL.test(nou)) return;
    setRedenumesc(true);
    setEroare("");
    try {
      await (alMeu ? api.redenumesteTronsonulMeu(vechi, nou)
                   : api.redenumesteTronson(projectId, vechi, nou));
      setDeRedenumit(null);
      if (cod === vechi) setCod(nou);
      setReincarcare((n) => n + 1);
    } catch (e) {
      const m = String((e as Error)?.message ?? e);
      setEroare(/cod_luat/.test(m) ? t("editor.cod_luat", { cod: nou })
        : /cod_invalid/.test(m) ? t("editor.cod_invalid") : m);
    } finally {
      setRedenumesc(false);
    }
  };

  /** PLANȘA UNUI MODUL CA PDF SAU DWG. Cartonașul are doar vederea derivată
   *  (`alpha`), nu documentul, deci documentul se cere atunci: cel publicat,
   *  sau ciorna dacă asta e ce arată cartonașul. */
  const exporta = async (m: api.Tronson, format: FormatExport) => {
    setEroare("");
    try {
      const ciorna = m.status === "draft";
      const r = await (alMeu ? api.getTronsonulMeu(m.code, { ciorna })
                             : api.getTronson(projectId, m.code, { ciorna }));
      await exportaTronson(format, r.doc as TronsonDoc, r);
    } catch (e) {
      setEroare(`${t(format === "pdf" ? "export.esuat" : "export.dwg_esuat")} `
        + String((e as Error)?.message ?? e));
    }
  };

  useEffect(() => {
    let viu = true;
    (alMeu ? api.listTronsoaneleMele() : api.listTronsoane(projectId))
      .then((c) => viu && setColectie(c as api.ColectieTronsoane))
      .catch((e) => viu && setEroare(String(e?.message ?? e)));
    // VOCABULARUL, ÎN ACELAȘI PAS. E o listă de câteva zeci de cuvinte și se
    // cere odată cu colecția, nu la deschiderea panoului: altfel prima apăsare
    // pe «+ etichetă» ar arăta o listă goală cât ține cererea, adică exact în
    // clipa în care omul se uită să vadă din ce alege. Un vocabular gol e un
    // răspuns («n-ai niciuna încă»), deci eroarea lui nu oprește colecția.
    (alMeu ? api.listEtichetleMele() : api.listEtichete(projectId))
      .then((r) => viu && setPosibile(r.etichete))
      .catch(() => viu && setPosibile([]));
    return () => { viu = false; };
  }, [projectId, alMeu, reincarcare]);

  /** Etichetele unui modul, scrise pe server, apoi colecția recitită.
   *
   *  RECITITĂ, nu peticită pe loc: numărătorul fiecărei etichete din vocabular
   *  se schimbă odată cu scrierea, iar cartonașul și lista din panou trebuie să
   *  spună același lucru. Colecția e câțiva kiloocteți — `alpha`, nu documente. */
  const scrieEtichetele = async (codul: string, etichete: string[]) => {
    setEroare("");
    setScriuEtichete(true);
    try {
      await (alMeu ? api.puneEticheteleMele(codul, etichete)
                   : api.puneEtichetele(projectId, codul, etichete));
      setReincarcare((n) => n + 1);
    } catch (e) {
      setEroare(String((e as Error)?.message ?? e));
    } finally {
      setScriuEtichete(false);
    }
  };

  /** RENUNȚĂ LA CIORNELE PROIECTULUI pe un modul — aceeași cale ca butonul
   *  din editor (v. `Editor::stergeCiorna`), cu aceeași întrebare: se pierd
   *  toate versiunile proiectului pe cod, și nu se poate anula. */
  const stergeCiorna = async (codul: string) => {
    if (!window.confirm(t("colectie.sterge_ciorna_intrebare", { cod: codul }))) return;
    setEroare("");
    try {
      await (alMeu ? api.stergeTronsonulMeu(codul)
                   : api.stergeTronson(projectId, codul));
      if (cod === codul) setCod(null);
      setNota(t("colectie.ciorna_stearsa", { cod: codul }));
      setReincarcare((n) => n + 1);
    } catch (e) {
      setEroare(String((e as Error)?.message ?? e));
    }
  };

  useEffect(() => {
    if (!cod) { setDoc(null); return; }
    let viu = true;
    setIncarcDoc(true);
    // O EROARE VECHE NU ȚINE DE MODULUL NOU. Fără golirea asta, banda roșie de
    // la o apăsare care a picat rămânea pe ecran peste următoarea care a mers.
    setEroare("");
    // `ciorna: true`, fiindcă ASTA ARATĂ ȘI CARTONAȘUL. De când colecția
    // cuprinde ciornele, cererea fără el întreba altceva decât scria pe ecran:
    // lista scotea M9 din rândurile nepublicate ale proiectului, iar apăsarea pe
    // el cerea versiunea PUBLICATĂ, care nu există — «NOT FOUND» pe un modul pe
    // care tocmai ți-l arătase. Serverul cade oricum înapoi pe cea publicată
    // când proiectul n-are rând propriu, deci modulele globale vin la fel.
    (alMeu ? api.getTronsonulMeu(cod, { ciorna: true })
           : api.getTronson(projectId, cod, { ciorna: true }))
      .then((r) => {
        if (!viu) return;
        setDoc(r.doc as TronsonDoc);
        const d = r as unknown as {
          creat_de?: string; creat_la?: string; nota?: string };
        setProv({ creat_de: d.creat_de, creat_la: d.creat_la, nota: d.nota });
      })
      .catch((e) => viu && setEroare(String(e?.message ?? e)))
      .finally(() => { if (viu) setIncarcDoc(false); });
    return () => { viu = false; };
  }, [projectId, alMeu, cod]);

  /** Fereastra «Tronson nou» — deschisă, e singura întrebare de pe ecran. */
  const [nou, setNou] = useState(false);

  /** Ciornele se văd implicit — altfel un șablon tocmai salvat ar lipsi din
   *  singurul loc în care cineva l-ar căuta. Comutatorul e pentru cealaltă
   *  întrebare, cea a compunerii: «din ce pot să fac o bară ACUM». */
  const [cuCiorne, setCuCiorne] = useState(true);

  const peCategorii = useMemo(() => {
    const m = (colectie?.modules ?? [])
      .filter((t) => cuCiorne || t.status !== "draft");
    return CATEGORII.map((c) => ({
      ...c,
      // Cu nucleu întâi: într-o categorie cu amândouă, modulul cu scară e cel
      // rar și cel care decide compoziția barei.
      module: m.filter((t) => t.category === c.cheie)
        .sort((a, b) => Number(b.has_core) - Number(a.has_core)
                        || a.code.localeCompare(b.code)),
    })).filter((c) => c.module.length);
  }, [colectie, cuCiorne]);

  return (
    <ObsHost className="ob-main">
      <div className="ob-pagina">
        <BandaDoarCitire />
        <div className="ob-acasa-head">
          <h2>{t("colectie.titlu")}</h2>
          <span className="ob-acasa-sub">{t("colectie.subtitlu")}</span>
          {/* UN MODUL NOU SE ÎNCEPE DE AICI, nu deschizând altul și ștergându-i
              desenul. Stă în capul colecției fiindcă asta e întrebarea pe care
              o pune cineva care se uită la ea și nu găsește ce-i trebuie. */}
          {potScrie && (
            <button type="button" className="btn ob-acasa-head-act"
                    onClick={() => setNou(true)}
                    title={t("nou.deschide_titlu")}>
              {t("nou.deschide")}
            </button>
          )}
        </div>

        <ImporturiInLucru
          projectId={projectId}
          pornite={pornite}
          laTerminat={useCallback((c: string) => {
            setPornite((p) => p.filter((x) => x.cod !== c));
            setReincarcare((n) => n + 1);
          }, [])}
          laOprit={useCallback((c: string) => {
            setPornite((p) => p.filter((x) => x.cod !== c));
            setReincarcare((n) => n + 1);
          }, [])} />

        {eroare && <div className="err">{eroare}</div>}
        {nota && <p className="note">{nota}</p>}
        {!colectie && !eroare && <p className="note">{t("comun.se_incarca")}</p>}

        {colectie && (
          <p className="ob-tr-provenienta">
            {colectie.own === 0
              ? t("colectie.toate_globale", { n: colectie.modules.length })
              : t("colectie.unele_ale_proiectului",
                  { ale: colectie.own, n: colectie.modules.length })}
            {colectie.band_depth_m != null
              && t("colectie.banda", { metri: metri(colectie.band_depth_m) })}
            {colectie.ciorne > 0 && (
              <>
                {" "}
                {tn("colectie.ciorne#n", colectie.ciorne)}
                {t("colectie.ciorne_explicatie")}
                <button type="button" className="ob-tr-filtru"
                        onClick={() => setCuCiorne((v) => !v)}>
                  {t(cuCiorne ? "colectie.ascunde_ciornele"
                              : "colectie.arata_ciornele")}
                </button>
              </>
            )}
          </p>
        )}

        {peCategorii.map((c) => (
          <section key={c.cheie} className="ob-tr-categorie">
            <div className="ob-tr-cat-cap">
              <PozitiaInBara cheie={c.cheie} />
              <h3 className="ob-tr-cat-titlu">{t(c.titlu)}</h3>
              <span className="ob-tr-cat-nr">{c.module.length}</span>
              <span className="ob-tr-cat-sub">{t(c.sub)}</span>
            </div>
            <div className="ob-tr-grila">
              {c.module.map((m) => (
                <Cartonas key={m.code} t={m} activ={m.code === cod}
                          laRedenumire={potScrie
                            && (alMeu || m.scope === "proiect")
                            ? () => setDeRedenumit(m.code)
                            : undefined}
                          la={() => setCod(m.code === cod ? null : m.code)}
                          laEditare={() => navigate(caleaEditorului(m.code))}
                          laStergere={potScrie && (alMeu
                            ? m.scope === "al_meu" : m.scope === "proiect")
                            ? () => stergeCiorna(m.code) : undefined}
                          laImpartire={alMeu && m.scope === "al_meu"
                            ? () => setDeImpartit(m.code) : undefined}
                          laExport={(f) => { void exporta(m, f); }} />
              ))}
            </div>
          </section>
        ))}

        {/* Modulul nou se deschide DE-A DREPTUL ÎN EDITOR: e gol, deci n-are ce
            arăta în colecție — tot ce urmează după naștere e desenul lui. */}
        {deRedenumit && (
          <DialogRedenumire cod={deRedenumit} lucrez={redenumesc}
                            laRenunta={() => setDeRedenumit(null)}
                            laNume={(n) => { void redenumeste(n); }} />
        )}

        {deImpartit && (
          <ImparteModul cod={deImpartit}
                        laInchidere={() => setDeImpartit(null)} />
        )}

        {nou && (
          <TronsonNou projectId={projectId}
                      laInchidere={() => setNou(false)}
                      laCreat={(c) => navigate(caleaEditorului(c))}
                      laPornit={(c, cerere) => {
                        // NU SE PLEACĂ SPRE EDITOR: modulul încă nu există.
                        // Fereastra se închide pe loc, bara rămâne pe pagină,
                        // iar omul poate porni încă una.
                        setNou(false);
                        setPornite((p) => [...p, { cod: c, la: Date.now() }]);
                        // Cererea pleacă nelegată de fereastră: dacă pică, se
                        // spune tot pe bară, nu într-o fereastră care nu mai e.
                        void cerere.catch((e) => {
                          const m = String((e as Error)?.message ?? e);
                          setPornite((p) => p.map((x) => x.cod === c
                            ? { ...x, eroare: /cod_luat/.test(m)
                                ? t("editor.cod_luat", { cod: c }) : m }
                            : x));
                        });
                      }} />
        )}

        {cod && (
          <section className="ob-tr-deschis">
            {incarcDoc && (
              <p className="note">{t("colectie.se_incarca_modulul", { cod })}</p>
            )}
            {doc && (
              <Vizualizator
                doc={doc} cod={cod}
                etichete={colectie?.modules.find((m) => m.code === cod)?.etichete ?? []}
                posibile={posibile} lucrez={scriuEtichete}
                laEtichete={potScrie
                  ? (et) => { void scrieEtichetele(cod, et); } : undefined} />
            )}
            {doc && (
              <Provenienta
                doc={doc}
                autor={colectie?.modules.find((m) => m.code === cod)?.autor}
                creatDe={prov.creat_de} creatLa={prov.creat_la}
                nota={prov.nota} />
            )}
          </section>
        )}
      </div>
    </ObsHost>
  );
}
